// @ts-nocheck
import styled from '@emotion/native';
import { FormCard, Skeleton, SkeletonProvider, Visible } from 'design-system';
import type React from 'react';
import {
  type ElementType,
  type PropsWithChildren,
  type ReactElement,
  useCallback,
} from 'react';
import type { ViewProps } from 'react-native';
import type {
  Row as RowType,
  TableBodyPropGetter,
  TableBodyProps,
} from 'react-table';

import type { PositionProps } from './PositionProps';
import { PressableWrapper } from './PressableWrapper';

type TableBodyPropGetterFunction<Data extends object> = (
  propGetter?: TableBodyPropGetter<Data> | undefined,
) => TableBodyProps;

// ADD: showLoadingSpinner for SkeletonProvider
interface DataTableBodyProps<Data extends object> {
  rows: RowType<Data>[];
  columnConfig: BodyColumnConfigProps<Data>;
  getTableBodyProps: TableBodyPropGetterFunction<Data>;
  prepareRow: (row: RowType<Data>) => void;
  Body: ElementType;
  Row: ElementType<PositionProps>;
  Cell: ElementType<PositionProps>;
  isLoading?: boolean;
  useCards?: boolean;
  onRowPress?: (data: Data) => void;
}

export interface BodyColumnConfig {
  flex?: number;
}

export type BodyColumnConfigProps<Data extends object> = Partial<
  Record<keyof Data, BodyColumnConfig>
>;

// ADD: showLoadingSpinner for SkeletonProvider
export const DataTableBody = <Data extends object>({
  rows,
  getTableBodyProps,
  prepareRow,
  Body,
  Row,
  Cell,
  isLoading = false,
  onRowPress,
  useCards = false,
}: PropsWithChildren<DataTableBodyProps<Data>>): ReactElement => {
  const onPress = useCallback(
    (data: Data) => {
      if (onRowPress) {
        onRowPress(data);
      }
    },
    [onRowPress],
  );

  interface RowConfig {
    row: RowType<Data>;
  }

  interface CellWrapperProps {
    width?: number;
  }

  const CellWrapper = styled.View<CellWrapperProps>(({ width }) => ({
    width,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    flexShrink: 1,
  }));

  const RowCard: React.FC<RowConfig> = ({ row }) => (
    <FormCard
      rows={row.cells.map((cell, cellIndex) => ({
        fields: [
          {
            type: 'custom',
            label: `${cell.render('header')}`,
            Display: () => (
              <Cell
                {...cell.getCellProps()}
                index={cellIndex}
                key={cell.row.id}
              >
                {cell.render('Cell')}
              </Cell>
            ),
          },
        ],
      }))}
    />
  );

  return (
    <SkeletonProvider loading={isLoading}>
      <Body {...getTableBodyProps()}>
        {rows.map((row, rowIndex) => {
          prepareRow(row);

          const onPressHandler = onRowPress && (() => onPress(row.original));
          return (
            <PressableWrapper key={rowIndex} onPress={onPressHandler}>
              <Row {...row.getRowProps()} key={row.id} index={rowIndex}>
                <Visible if={useCards}>
                  <RowCard row={row} />
                </Visible>
                <Visible if={!useCards}>
                  {row.cells.map((cell, cellIndex) => {
                    return (
                      <Skeleton key={cellIndex}>
                        <CellWrapper
                          {...(cell.getCellProps() as ViewProps)}
                          key={cell.column.id}
                          width={Number(cell.column.width)}
                        >
                          <Cell
                            {...cell.getCellProps()}
                            index={cellIndex}
                            key={cell.row.id}
                          >
                            {cell.render('Cell')}
                          </Cell>
                        </CellWrapper>
                      </Skeleton>
                    );
                  })}
                </Visible>
              </Row>
            </PressableWrapper>
          );
        })}
      </Body>
    </SkeletonProvider>
  );
};
